import { useCallback, useEffect, useRef, useState } from 'react'
import {
  listDocuments,
  uploadDocument,
  deleteDocument,
  downloadDocument,
  SUPPORTED_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  type KbDocument,
  type Scope,
  type LinkedPage,
  type KbLimits,
  ingestUrl,
  setDocumentScope,
} from '../api/knowledge'
import type { MeResponse } from '../types'

/**
 * Who can see one saved item, and the means to change it.
 *
 * A select rather than a toggle: the two answers are not on and off, and
 * "Everyone" is worth reading on a row that has it.
 */
function ScopePicker({
  doc,
  canChoose,
  busy,
  onChange,
}: {
  doc: KbDocument
  /** Whether this agent may decide who sees it: the administrator, only. */
  canChoose: boolean
  busy: boolean
  onChange: (source: string, next: Scope) => void
}) {
  // Not yours to decide: say who can see it and leave it alone. Putting a
  // document on the team's shelf is the administrator's to do, and an offer
  // the server will refuse is worse than no offer at all.
  if (!canChoose) {
    return <span className="doc-url">{doc.scope === 'personal' ? 'Only me' : 'Everyone'}</span>
  }

  return (
    <select
      className="scope-pick"
      value={doc.scope}
      disabled={busy}
      aria-label={`Who can see ${doc.source}`}
      onChange={(e) => onChange(doc.source, e.target.value as Scope)}
    >
      <option value="org">Everyone</option>
      <option value="personal">Only me</option>
    </select>
  )
}

/** The address without the parts every address has. */
function shortUrl(url: string) {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '')
}

function sizeMb(bytes: number) {
  return (bytes / 1024 / 1024).toFixed(1)
}

export function KnowledgeSection({
  me,
  isAdmin,
}: {
  me: MeResponse | null
  /** Whether this agent runs the help desk, per Zoho's own profiles. */
  isAdmin: boolean
}) {
  const [docs, setDocs] = useState<KbDocument[]>([])
  const [limits, setLimits] = useState<KbLimits | null>(null)
  const [loading, setLoading] = useState(true)
  // Which action is running, not merely that one is: a single flag put
  // "Reading…" on the file button while a page was being fetched.
  const [busy, setBusy] = useState<'' | 'file' | 'page' | 'row'>('')
  const [url, setUrl] = useState('')
  const [describePictures, setDescribePictures] = useState(false)
  const [linked, setLinked] = useState<LinkedPage | null>(null)
  const [error, setError] = useState('')
  const [scope, setScope] = useState<Scope>(isAdmin ? 'org' : 'personal')
  // The address of each linked page, editable in place. Keyed by the stored
  // source so an edit survives the list re-sorting under it.
  const [edits, setEdits] = useState<Record<string, string>>({})
  // Which page is being re-addressed. One at a time: every row holding an open
  // text box made a saved list look like an unfinished form.
  const [editing, setEditing] = useState<string | null>(null)
  // Every upload is documentation. The picker that used to set this asked a
  // filing question the agent had no reason to care about.
  const sourceType = 'doc'
  const fileRef = useRef<HTMLInputElement>(null)

  const refresh = useCallback(async () => {
    try {
      const res = await listDocuments()
      setDocs(res.documents)
      setLimits(res.limits)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Check here as well as on the server: a 5MB file should not be read into
    // memory and base64'd only to be refused.
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`${file.name} is ${sizeMb(file.size)}MB; the limit is 5MB.`)
      e.target.value = ''
      return
    }

    setBusy('file')
    setError('')
    try {
      await uploadDocument(file, scope, sourceType)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy('')
      e.target.value = ''
    }
  }

  /**
   * Share a saved item with the org, or take it back to yourself.
   *
   * Offered on the row rather than only at the moment of adding: who should
   * see a document is a thing that changes, and re-uploading to change it is
   * not an answer.
   */
  /**
   * Yours, or anything the team shares when you run the help desk.
   *
   * A personal document is only ever listed to the person it belongs to —
   * that is what the server's visibility rule means — so seeing one is proof
   * enough. Comparing ids as well made a row that is plainly the agent's own
   * look like someone else's when the two were not the same string.
   */
  function canManage(d: KbDocument) {
    return d.scope === 'personal' || d.uid === me?.uid || (isAdmin && d.scope === 'org')
  }

  async function changeScope(source: string, next: Scope) {
    setBusy('row')
    setError('')
    try {
      await setDocumentScope(source, next)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy('')
    }
  }

  async function remove(source: string) {
    setBusy('row')
    setError('')
    try {
      await deleteDocument(source)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy('')
    }
  }

  /**
   * Point a linked page at a different address.
   *
   * The new page is read first and the old entry removed only once that has
   * worked. The other order is quicker to write and loses the agent's page
   * whenever the new address turns out to be unreachable.
   */
  async function changeLink(doc: KbDocument, next: string) {
    setBusy('page')
    setError('')
    setLinked(null)
    try {
      const page = await ingestUrl(next.trim(), doc.scope, describePictures, doc.source)
      // A page whose title has not changed was replaced in place; deleting the
      // old source would then delete the page just added.
      if (page.source !== doc.source) await deleteDocument(doc.source)
      setEdits((was) => ({ ...was, [doc.source]: '' }))
      setLinked(page)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
    }
  }

  async function addLink() {
    setBusy('page')
    setError('')
    setLinked(null)
    try {
      const page = await ingestUrl(url.trim(), scope, describePictures)
      // Clearing the box is what greys the button out again, which on its own
      // reads as "nothing happened" — hence the receipt below it.
      setUrl('')
      setLinked(page)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
    }
  }

  // A full allowance greys the button, and the count beside it says why. The
  // alternative is an agent choosing a file, waiting for the upload, and being
  // told afterwards that it was never going to be accepted.
  // A file was uploaded and sits still; a page points at a website that changes
  // underneath it. Splitting them is what lets each show what matters about it.
  const files = docs.filter((d) => !d.url)
  const pages = docs.filter((d) => d.url)

  const docsFull = Boolean(limits && limits.documents.used >= limits.documents.max)
  const linksFull = Boolean(limits && limits.links.used >= limits.links.max)

  return (
    <div className="card">
      <h2>
        Knowledge base{' '}
        {limits && (
          <span className="ok">
            {docs.length} of {limits.documents.max + limits.links.max}
          </span>
        )}
      </h2>
      <p className="hint">
        Add your files and pages. Replies come from these, and from nothing else.
      </p>

      {error && <div className="error">{error}</div>}

      {/* Files and pages are different things — one is uploaded and sits
          still, the other points at a website that changes underneath it — and
          a single list made them look interchangeable. */}
      {loading ? (
        <p className="note">Loading…</p>
      ) : (
        <>
          {/* Before either control, because it decides what they do. At the
              bottom it read as a filter on the lists above it.
              Only whoever runs the help desk sees it: for everyone else there
              is no choice to make, and a question with one answer is noise. */}
          {isAdmin ? (
            <label className="field">
              <span>Add new files and pages for</span>
              <select value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
                <option value="org">Everyone in my Zoho org</option>
                <option value="personal">Only me</option>
              </select>
            </label>
          ) : (
            <p className="note">Only you see what you add here.</p>
          )}

          <div className="kb-head">
            <h3 className="group-name">Files</h3>
            {limits && (
              <span className={docsFull ? 'kb-count full' : 'kb-count'}>
                {limits.documents.used} of {limits.documents.max}
                {docsFull && ' · full'}
              </span>
            )}
          </div>

          {/* The browser's own file control is a white button with "No file
              chosen" beside it, which belongs to the browser and not to this
              panel. The input still does the work; the label is what shows. */}
          {docsFull ? (
            <p className="note">Remove a file below to add another.</p>
          ) : (
            <label className="file-pick">
              <input
                ref={fileRef}
                type="file"
                accept={SUPPORTED_EXTENSIONS.map((e) => `.${e}`).join(',')}
                onChange={onPick}
                disabled={Boolean(busy)}
              />
              <span className="file-btn">{busy === 'file' ? 'Reading…' : 'Choose a file'}</span>
              <span className="note">PDF or Word, up to 5MB</span>
            </label>
          )}

          {files.length === 0 ? (
            <p className="note">Nothing uploaded yet.</p>
          ) : (
            <ul className="docs">
              {files.map((d) => (
                <li key={d.source}>
                  <div className="doc-lines">
                    {d.hasOriginal ? (
                      <button
                        className="linkish doc-name"
                        onClick={() => void downloadDocument(d.source)}
                      >
                        {d.source}
                      </button>
                    ) : (
                      <span className="doc-name">{d.source}</span>
                    )}
                    <ScopePicker
                      doc={d}
                      canChoose={isAdmin && canManage(d)}
                      busy={Boolean(busy)}
                      onChange={changeScope}
                    />
                  </div>
                  {canManage(d) && (
                    <button
                      className="linkish danger"
                      onClick={() => remove(d.source)}
                      disabled={Boolean(busy)}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <hr />

          <div className="kb-head">
            <h3 className="group-name">Webpages</h3>
            {limits && (
              <span className={linksFull ? 'kb-count full' : 'kb-count'}>
                {limits.links.used} of {limits.links.max}
                {linksFull && ' · full'}
              </span>
            )}
          </div>

          {/* At the limit the whole form goes, rather than sitting there
              disabled: a box that looks like a box and refuses the cursor is
              worse than no box, and the reason was three lines below it. */}
          {linksFull ? (
            <p className="note">Remove a page below to add another.</p>
          ) : (
            <>
              <label className="field">
                <span>Add a page</span>
                <input
                  type="url"
                  placeholder="https://yourcompany.com/help"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value)
                    setLinked(null)
                  }}
                  autoComplete="off"
                />
              </label>

              <label className="check">
                <input
                  type="checkbox"
                  checked={describePictures}
                  onChange={(e) => setDescribePictures(e.target.checked)}
                />
                Read screenshots
              </label>

              <button disabled={Boolean(busy) || !url.trim()} onClick={addLink}>
                {busy === 'page' ? 'Reading page…' : 'Add page'}
              </button>

              {/* Under the button that caused it. After the list, a receipt
                  for the page just added read as a note about the last row. */}
              {linked && (
                <p className="note done">
                  Added {linked.source} ({linked.chunks} section
                  {linked.chunks === 1 ? '' : 's'}
                  {linked.pictures > 0 &&
                    `, ${linked.pictures} picture${linked.pictures === 1 ? '' : 's'} read`}
                  ).
                </p>
              )}

              {linked?.pictureNote && <p className="note">{linked.pictureNote}</p>}
            </>
          )}

          {pages.length === 0 ? (
            <p className="note">No pages yet.</p>
          ) : (
            <ul className="docs">
              {pages.map((d) => {
                const current = edits[d.source] ?? d.url ?? ''
                const changed = current.trim() !== (d.url ?? '') && current.trim().length > 0

                // A row being re-addressed becomes the box it needs; the rest
                // stay rows, because a saved page is a thing, not a form.
                if (editing === d.source) {
                  return (
                    <li key={d.source} className="page-editing">
                      <div className="page-edit">
                        <input
                          type="url"
                          value={current}
                          disabled={Boolean(busy)}
                          autoFocus
                          aria-label={`Address for ${d.source}`}
                          onChange={(e) =>
                            setEdits((was) => ({ ...was, [d.source]: e.target.value }))
                          }
                        />
                        <button
                          disabled={Boolean(busy) || !changed}
                          onClick={() => void changeLink(d, current).then(() => setEditing(null))}
                        >
                          {busy === 'page' ? 'Reading…' : 'Save'}
                        </button>
                        <button
                          className="linkish"
                          disabled={Boolean(busy)}
                          onClick={() => {
                            setEdits((was) => ({ ...was, [d.source]: d.url ?? '' }))
                            setEditing(null)
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </li>
                  )
                }

                return (
                  <li key={d.source}>
                    <div className="doc-lines">
                      <span className="doc-name" title={d.url ?? d.source}>
                        {shortUrl(d.url ?? d.source)}
                      </span>
                      <ScopePicker
                      doc={d}
                      canChoose={isAdmin && canManage(d)}
                      busy={Boolean(busy)}
                      onChange={changeScope}
                    />
                    </div>
                    {canManage(d) && (
                      <>
                        <button
                          className="linkish"
                          disabled={Boolean(busy)}
                          onClick={() => setEditing(d.source)}
                        >
                          Edit
                        </button>
                        <button
                          className="linkish danger"
                          disabled={Boolean(busy)}
                          onClick={() => remove(d.source)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

        </>
      )}
    </div>
  )
}
